'use client'

/**
 * Error boundary for data-fetching sections.
 * Integrates with React Query's useQueryErrorResetBoundary for retry support.
 */
import React from 'react'

import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowLeft,
  FileQuestion,
  Home,
  RefreshCw,
  ServerCrash,
} from 'lucide-react'
import Link from 'next/link'

import { ApiError } from '@/context/usaspending/liveFetcher'

type ErrorType = 'not-found' | 'server-error' | 'unknown'

function getErrorType(error: Error): ErrorType {
  // Prefer structured ApiError status for reliable detection
  if (error instanceof ApiError) {
    if (error.status === 404 || error.status === 400) return 'not-found'
    if (error.status >= 500) return 'server-error'
  }

  // Fallback: string matching for non-ApiError errors
  const message = error.message?.toLowerCase() || ''
  if (message.includes('404') || message.includes('not found')) {
    return 'not-found'
  }
  if (
    message.includes('500') ||
    message.includes('server') ||
    message.includes('internal')
  ) {
    return 'server-error'
  }
  return 'unknown'
}

export function isServiceWaking(error: unknown): boolean {
  // Primary: duck-type check for ServiceWakingError
  if (
    typeof error === 'object' &&
    error !== null &&
    'retryReason' in error &&
    (error as Record<string, unknown>).retryReason === 'clickhouse-waking'
  ) {
    return true
  }
  // Fallback: ApiError with 503 status (e.g., CORS stripped the custom headers)
  if (error instanceof ApiError && error.status === 503) {
    return true
  }
  return false
}

// -- Service unavailable notice (shown when ClickHouse is down) --

function ServiceUnavailableNotice() {
  return (
    <div className="flex items-center justify-center py-10">
      <p className="text-sm text-primary-500 dark:text-primary-400">
        Analytics data is temporarily unavailable. Please refresh the page in a
        few minutes.
      </p>
    </div>
  )
}

// -- Not-found page (full-page variant) --

interface NotFoundPageProps {
  title: string
  description: string
  backHref?: string
  backLabel?: string
}

function NotFoundPage({
  title,
  description,
  backHref,
  backLabel,
}: NotFoundPageProps) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center p-8">
      <div className="flex max-w-lg flex-col items-center text-center">
        <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-800">
          <FileQuestion className="h-10 w-10 text-primary-500 dark:text-primary-400" />
        </div>
        <h1 className="mb-3 text-2xl font-bold text-primary-900 dark:text-primary-100">
          {title}
        </h1>
        <p className="mb-8 text-base leading-relaxed text-primary-600 dark:text-primary-400">
          {description}
        </p>
        <div className="flex items-center gap-3">
          {backHref && (
            <Link
              href={backHref}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-900 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-primary-800 hover:shadow-md dark:bg-primary-100 dark:text-primary-900 dark:hover:bg-primary-200"
            >
              <ArrowLeft className="h-4 w-4" />
              {backLabel || 'Go Back'}
            </Link>
          )}
          <Link
            href="/"
            className="inline-flex items-center gap-2 rounded-lg border border-primary-300 bg-white px-5 py-2.5 text-sm font-medium text-primary-700 shadow-sm transition-all hover:bg-primary-50 hover:shadow-md dark:border-primary-600 dark:bg-primary-800 dark:text-primary-300 dark:hover:bg-primary-700"
          >
            <Home className="h-4 w-4" />
            Homepage
          </Link>
        </div>
      </div>
    </div>
  )
}

// -- Not-found card (section-level variant) --

interface NotFoundCardProps {
  title: string
  description: string
}

function NotFoundCard({ title, description }: NotFoundCardProps) {
  return (
    <div className="flex flex-col items-center justify-center p-8">
      <div className="flex max-w-md flex-col items-center rounded-2xl border border-primary-200 bg-white p-8 text-center shadow-sm dark:border-primary-700 dark:bg-primary-900">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-800">
          <FileQuestion className="h-8 w-8 text-primary-500 dark:text-primary-400" />
        </div>
        <h3 className="mb-2 text-xl font-semibold text-primary-900 dark:text-primary-100">
          {title}
        </h3>
        <p className="text-sm leading-relaxed text-primary-600 dark:text-primary-400">
          {description}
        </p>
      </div>
    </div>
  )
}

// -- Retryable error card (server error / unknown) --

interface ErrorDisplayProps {
  title: string
  description: string
  icon: React.ElementType
  onRetry: () => void
}

function ErrorContent({
  title,
  description,
  icon: Icon,
  onRetry,
}: ErrorDisplayProps) {
  return (
    <div className="w-full max-w-sm overflow-hidden rounded-lg border border-primary-200 bg-white shadow-sm dark:border-primary-800 dark:bg-primary-900">
      <div className="flex items-start gap-3 p-5">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-red-500 dark:text-red-400" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-primary-900 dark:text-primary-100">
            {title}
          </p>
          <p className="mt-0.5 text-sm leading-relaxed text-primary-500 dark:text-primary-400">
            {description}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-end border-t border-primary-100 bg-primary-50 px-5 py-3 dark:border-primary-800 dark:bg-primary-900/50">
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-2 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600"
        >
          <RefreshCw className="h-3 w-3" />
          Try again
        </button>
      </div>
    </div>
  )
}

function ErrorCard(props: ErrorDisplayProps) {
  return (
    <div className="flex flex-col items-center justify-center p-8">
      <ErrorContent {...props} />
    </div>
  )
}

function ErrorPage(props: ErrorDisplayProps) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center p-8">
      <ErrorContent {...props} />
    </div>
  )
}

// -- Public fallback component --

interface DataErrorFallbackProps {
  error: Error
  resetErrorBoundary: () => void
  title?: string
  /** When true, renders as a full-page centered error */
  fullPage?: boolean
  /** Title for not-found errors (e.g., "Award Not Found") */
  notFoundTitle?: string
  /** Link to navigate back (e.g., "/awards/search") */
  backHref?: string
  /** Label for back link (e.g., "Browse Awards") */
  backLabel?: string
}

export function DataErrorFallback({
  error,
  resetErrorBoundary,
  title,
  fullPage = false,
  notFoundTitle,
  backHref,
  backLabel,
}: DataErrorFallbackProps) {
  const errorType = getErrorType(error)

  if (errorType === 'not-found') {
    const displayTitle = notFoundTitle || title || 'Not Found'
    const description =
      'The requested data could not be found. It may have been moved or deleted.'

    return fullPage ? (
      <NotFoundPage
        title={displayTitle}
        description={description}
        backHref={backHref}
        backLabel={backLabel}
      />
    ) : (
      <NotFoundCard title={displayTitle} description={description} />
    )
  }

  const isServerError = errorType === 'server-error'
  const errorProps: ErrorDisplayProps = {
    title: title || (isServerError ? 'Something Went Wrong' : 'Failed to Load'),
    description: isServerError
      ? 'We encountered a server error. Please try again.'
      : error.message || 'Something went wrong while loading this section.',
    icon: isServerError ? ServerCrash : AlertTriangle,
    onRetry: resetErrorBoundary,
  }
  return fullPage ? (
    <ErrorPage {...errorProps} />
  ) : (
    <ErrorCard {...errorProps} />
  )
}

// -- Error boundary class --

interface DataErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

interface DataErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ReactNode
  /** Title shown in default error fallback */
  title?: string
  /** Called when error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
  /** Custom render function for error state */
  renderError?: (error: Error, reset: () => void) => React.ReactNode
  /** When true, renders as a full-page centered error */
  fullPage?: boolean
  /** Title for not-found errors (e.g., "Award Not Found") */
  notFoundTitle?: string
  /** Link to navigate back (e.g., "/awards/search") */
  backHref?: string
  /** Label for back link (e.g., "Browse Awards") */
  backLabel?: string
  /** Rethrow not-found errors so a parent boundary can handle them */
  rethrowNotFound?: boolean
  /** Custom fallback for service-unavailable (503) errors instead of generic notice */
  serviceUnavailableFallback?: React.ReactNode
}

/**
 * Error boundary for data-fetching sections.
 * Use with Suspense for complete error + loading handling:
 *
 * ```tsx
 * <DataErrorBoundary title="Failed to load transactions">
 *   <Suspense fallback={<TransactionsSkeleton />}>
 *     <TransactionsTab id={id} />
 *   </Suspense>
 * </DataErrorBoundary>
 * ```
 */
export class DataErrorBoundary extends React.Component<
  DataErrorBoundaryProps,
  DataErrorBoundaryState
> {
  constructor(props: DataErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): DataErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError?.(error, errorInfo)

    if (process.env.NODE_ENV !== 'production') {
      console.error('DataErrorBoundary caught error:', error)
    }
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      // Show unavailable notice when ClickHouse is down
      if (isServiceWaking(this.state.error)) {
        return this.props.serviceUnavailableFallback !== undefined ? (
          this.props.serviceUnavailableFallback
        ) : (
          <ServiceUnavailableNotice />
        )
      }

      // Rethrow not-found errors so a parent boundary can render the full-page 404
      if (
        this.props.rethrowNotFound &&
        getErrorType(this.state.error) === 'not-found'
      ) {
        throw this.state.error
      }

      if (this.props.renderError) {
        return this.props.renderError(this.state.error, this.resetErrorBoundary)
      }

      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <DataErrorFallback
          error={this.state.error}
          resetErrorBoundary={this.resetErrorBoundary}
          title={this.props.title}
          fullPage={this.props.fullPage}
          notFoundTitle={this.props.notFoundTitle}
          backHref={this.props.backHref}
          backLabel={this.props.backLabel}
        />
      )
    }

    return this.props.children
  }
}

/**
 * Hook + ErrorBoundary combo that automatically resets queries on retry.
 *
 * Wraps DataErrorBoundary with React Query's useQueryErrorResetBoundary
 * so the query cache is invalidated when the user clicks "Try Again".
 * Props flow directly to DataErrorBoundary — no renderError duplication.
 */
export function QueryErrorBoundary(props: {
  children: React.ReactNode
  title?: string
  fallback?: React.ReactNode
  fullPage?: boolean
  notFoundTitle?: string
  backHref?: string
  backLabel?: string
  rethrowNotFound?: boolean
  /** Custom fallback for service-unavailable (503) errors instead of generic notice */
  serviceUnavailableFallback?: React.ReactNode
}) {
  const { reset } = useQueryErrorResetBoundary()
  const { children, serviceUnavailableFallback, ...rest } = props

  return (
    <DataErrorBoundary
      {...rest}
      serviceUnavailableFallback={serviceUnavailableFallback}
      renderError={(error, resetBoundary) => (
        <DataErrorFallback
          error={error}
          {...rest}
          resetErrorBoundary={() => {
            reset()
            resetBoundary()
          }}
        />
      )}
    >
      {children}
    </DataErrorBoundary>
  )
}
